/**
 * Betroffene Tage der Vorschau (Board „Wirkung prüfen“): nur Tage mit Änderung, Warnung oder Sperre,
 * erst acht, dann „weitere anzeigen“. Je Tag Preis und Mindestnächte bestätigt → neu mit Grund,
 * Höchstaufenthalt und Herleitung des Mindestaufenthalts. Am schmalen Container Karten statt Tabelle.
 */
import { useState } from 'react';
import type { PricingConfigurationPreviewDay } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';
import { MinimumStayDecisionExplanation } from '../MinimumStayDecisionExplanation';
import { minimumStayText, reasonText } from '../pricingCodeText';
import { affectedDays, dayLabel, dayResults, type DayResult } from './previewModel';

const STEP = 8;
const TONE: Record<DayResult, string> = {
  Änderung: 'bg-forest-soft text-forest-deep',
  Warnung: 'bg-amber-tint text-amber-ink',
  Blockiert: 'bg-sealwax-tint text-sealwax',
  Unverändert: 'bg-card-alt text-ink-soft',
};
const MAX_STAY: Record<string, string> = {
  Conflict: 'Konflikt',
  Effective: 'wirksame Grenze',
  NotSet: 'nicht gesetzt',
};

export function PricingPreviewDays({
  days,
  currency,
}: {
  days: PricingConfigurationPreviewDay[];
  currency: string;
}) {
  const [shown, setShown] = useState(STEP);
  const affected = affectedDays(days);
  if (affected.length === 0) {
    return <p className="text-ink-soft text-sm">An keinem Tag ändert sich etwas.</p>;
  }
  const visible = affected.slice(0, shown);
  const rest = affected.length - visible.length;
  const money = (value: number | null) => (value === null ? '—' : formatMoney(value, currency));
  const cells = (day: PricingConfigurationPreviewDay) => {
    return [
      <span key="date" className="font-mono text-xs">
        {dayLabel(day.date)}
      </span>,
      <Fact
        key="rate"
        value={
          day.desiredRate === null
            ? money(day.confirmedRate)
            : `${money(day.confirmedRate)} → ${money(day.desiredRate)}`
        }
        why={reasonText(day.rateCode) ?? day.rateCode}
      />,
      <span key="stay" className="flex min-w-0 flex-col">
        <Fact
          value={`${day.confirmedMinimumStay ?? '—'} → ${day.desiredMinimumStay ?? '—'}`}
          why={minimumStayText(day.minimumStayCode) ?? day.minimumStayCode}
        />
        <span className="text-ink-soft text-[11px]">
          Höchstaufenthalt {day.effectiveMaximumStay ?? '—'} ·{' '}
          {MAX_STAY[day.maximumStayStatus] ?? day.maximumStayStatus}
        </span>
        <MinimumStayDecisionExplanation explanation={day.minimumStayExplanation} />
      </span>,
      <span key="result" className="flex flex-wrap gap-1 self-start">
        {dayResults(day).map((result) => (
          <span
            key={result}
            className={cn('rounded-pill px-2.5 py-0.5 text-[11px] font-bold', TONE[result])}
          >
            {result}
          </span>
        ))}
      </span>,
    ];
  };
  return (
    <section className="flex flex-col gap-2">
      <span className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        Betroffene Tage
      </span>
      <table className="border-hairline @3xl:table hidden w-full table-fixed border-separate border-spacing-0 overflow-hidden rounded-lg border text-[13px]">
        <thead className="bg-paper text-ink-soft font-mono text-[10px] uppercase tracking-[0.12em]">
          <tr>
            {['Datum', 'Preis bestätigt → neu', 'Mindestnächte', 'Ergebnis'].map((label) => (
              <th key={label} scope="col" className="px-3.5 py-2.5 text-left font-medium">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((day) => (
            <tr key={day.date}>
              {cells(day).map((cell, index) => (
                <td key={index} className="border-card-alt border-t px-3.5 py-2.5 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="@3xl:hidden flex flex-col gap-2">
        {visible.map((day) => (
          <li key={day.date} className="border-hairline flex flex-col gap-1 rounded-lg border p-3">
            {cells(day)}
          </li>
        ))}
      </ul>
      {rest > 0 && (
        <button
          type="button"
          onClick={() => setShown(shown + 20)}
          className="text-forest min-h-11 self-start text-[13px] font-bold"
        >
          … und {rest} weitere betroffene {rest === 1 ? 'Tag' : 'Tage'} – weitere anzeigen
        </button>
      )}
    </section>
  );
}

function Fact({ value, why }: { value: string; why: string }) {
  return (
    <span className="flex min-w-0 flex-col">
      <b className="tabular-nums">{value}</b>
      <span className="text-ink-soft text-[11px]">{why}</span>
    </span>
  );
}
